import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  styled,
  LinearProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

const StyledLinearProgress = styled(LinearProgress)(({ color }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: "#f0f0f0",
  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
    backgroundColor: color,
  },
}));

const ScoreItem = ({ label, score, color }) => {
  const safeScore = Number.isFinite(Number(score)) ? Number(score) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography
          variant="body1"
          color="rgb(75 85 99 / var(--tw-text-opacity, 1))"
          fontSize={"0.875rem"}
        >
          {label || ""}
        </Typography>
        <Typography variant="body1" fontWeight={"600"} fontSize={"0.875rem"}>
          {`${Math.round(safeScore)}%`}
        </Typography>
      </Box>
      <StyledLinearProgress
        variant="determinate"
        value={safeScore}
        color={color}
      />
    </Box>
  );
};

function Sentiment({ sentimentData }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hover, setHover] = useState(null);

  // ---- SAFE DATA NORMALIZATION ----
  const rawTrend = Array.isArray(sentimentData?.sentimentTrend)
    ? sentimentData.sentimentTrend
    : [];

  // coerce values to numbers & drop bad items
  const cleanData = rawTrend
    .map((d) => ({ ...d, value: Number(d?.value) }))
    .filter((d) => Number.isFinite(d.value));

  const width = isMobile ? 300 : 400;
  const height = 200;
  const padding = 40;

  const values = cleanData.map((d) => d.value);
  const maxValue = values.length ? Math.max(...values) : 0;
  const minValue = values.length ? Math.min(...values) : 0;
  const range = maxValue - minValue || 1;

  // avoid divide-by-zero when <2 points
  const denom = Math.max(cleanData.length - 1, 1);

  const xy = (item, index) => {
    const x = padding + (index * (width - 2 * padding)) / denom;
    const y =
      height -
      padding -
      ((item.value - minValue) / range) * (height - 2 * padding);
    return { x, y };
  };

  const createPath = () => {
    if (cleanData.length === 0) return "";
    const pts = cleanData.map((item, i) => {
      const { x, y } = xy(item, i);
      return `${x},${y}`;
    });
    return `M ${pts.join(" L ")}`;
  };

  const createAreaPath = () => {
    if (cleanData.length === 0) return "";
    if (cleanData.length === 1) {
      const { x } = xy(cleanData[0], 0);
      // Degenerate area to a vertical line at x and baseline
      return `M ${x},${height - padding} L ${x},${height - padding} Z`;
    }
    const pts = cleanData.map((item, i) => {
      const { x, y } = xy(item, i);
      return `${x},${y}`;
    });
    const firstX = pts[0].split(",")[0];
    const lastX = pts[pts.length - 1].split(",")[0];
    return `M ${firstX},${height - padding} L ${pts.join(" L ")} L ${lastX},${
      height - padding
    } Z`;
  };

  // ---- DISTRIBUTION (guard NaNs/Bounds) ----
  const size = 200;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const pct = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, Math.min(100, v));
  };

  const positive = pct(sentimentData?.sentimentPercentages?.positive);
  const neutral = pct(sentimentData?.sentimentPercentages?.neutral);
  const negative = pct(sentimentData?.sentimentPercentages?.negative);

  const positiveLength = (positive / 100) * circumference;
  const neutralLength = (neutral / 100) * circumference;
  const negativeLength = (negative / 100) * circumference;

  const e = sentimentData?.emotionAnalysis ?? {};
  const parameterData = [
    { label: "Happiness", score: pct(e.Happiness), color: "#10B981" },
    { label: "Calmness", score: pct(e.Calmness), color: "#3B82F6" },
    { label: "Frustration", score: pct(e.Frustration), color: "#EF4444" },
    { label: "Empathy", score: pct(e.Empathy), color: "#A855F7" },
    { label: "Confidence", score: pct(e.Confidence), color: "#EAB308" },
  ];

  // const average = ((positive + neutral + negative) / 3).toFixed(2);

  const FeedbackSection = styled(Box)(({ theme, variant }) => ({
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: 5,
    backgroundColor: variant === "strengths" ? "#f0fdf4" : "#fffbeb",
    border: variant === "strengths" ? "1px solid #bbf7d0" : "1px solid #fde68a",
    "&:last-child": {
      marginBottom: 0,
    },
  }));

  const SectionHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  }));

  const FeedbackList = styled("ul")(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(5),
    "& li": {
      fontSize: "0.75rem",
      color: "#6b7280",
      marginBottom: theme.spacing(0.5),
      "&:last-child": {
        marginBottom: 0,
      },
    },
  }));

  const noTrend = cleanData.length === 0;

  return (
    <div>
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid
          item
          size={{ xs: 12, md: 6 }}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Paper
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              flex: 1,
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              fontWeight="600"
              fontSize={"1.125rem"}
            >
              Sentiment Distribution
            </Typography>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <svg
                width={size}
                height={size}
                style={{ transform: "rotate(-90deg)" }}
              >
                {/* Background circle */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="#f3f4f6"
                  strokeWidth={strokeWidth}
                />

                {/* Positive segment */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${positiveLength} ${circumference}`}
                  strokeDashoffset={0}
                  strokeLinecap="round"
                />

                {/* Neutral segment */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${neutralLength} ${circumference}`}
                  strokeDashoffset={-positiveLength}
                  strokeLinecap="round"
                />

                {/* Negative segment */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${negativeLength} ${circumference}`}
                  strokeDashoffset={-(positiveLength + neutralLength)}
                  strokeLinecap="round"
                />
              </svg>

              <Box
                sx={{
                  position: "absolute",
                  top: "45%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h4"
                  component="div"
                  fontWeight="bold"
                  color="#374151"
                >
                  {sentimentData?.overallSentimentScore?`${Math.round(sentimentData?.overallSentimentScore)}%`:0}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "rgb(75 85 99 / var(--tw-text-opacity, 1))" }}
                  >
                    {`Positive: ${positive}%`}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "#f59e0b",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "rgb(75 85 99 / var(--tw-text-opacity, 1))" }}
                  >
                    {`Neutral: ${neutral}%`}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "#ef4444",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "rgb(75 85 99 / var(--tw-text-opacity, 1))" }}
                  >
                    {`Negative: ${negative}%`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
        {/* Sentiment Trend */}
        <Grid
          item
          size={{ xs: 12, md: 6 }}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Paper
            sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, flex: 1 }}
          >
            <Typography
              variant="h6"
              component="h2"
              fontWeight="600"
              fontSize={"1.125rem"}
            >
              Sentiment Trend
            </Typography>

            {noTrend ? (
              <Box
                sx={{
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6b7280",
                }}
              >
                <Typography variant="body2">
                  No sentiment trend data available.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: "100%" }}>
                {/* ⬇️ NEW inner box: positions tooltip relative to the SVG only */}
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: `${height}px`,
                  }}
                >
                  <svg
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${width} ${height}`}
                    preserveAspectRatio="none"
                    onMouseLeave={() => setHover(null)}
                  >
                    {/* Filled area */}
                    <path d={createAreaPath()} fill="rgba(16, 185, 129, 0.1)" />

                    {/* Line */}
                    <path
                      d={createPath()}
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />

                    {/* Vertical hover guide line */}
                    {hover && (
                      <line
                        x1={hover.x}
                        y1={padding - 6}
                        x2={hover.x}
                        y2={height - padding + 6}
                        stroke="#10b981"
                        strokeDasharray="4 4"
                        strokeWidth="1"
                        opacity="0.6"
                      />
                    )}

                    {/* Data points */}
                    {cleanData.map((item, index) => {
                      const { x, y } = xy(item, index);
                      const active = hover?.index === index;
                      return (
                        <g
                          key={index}
                          onMouseEnter={() => setHover({ index, x, y })}
                        >
                          <circle cx={x} cy={y} r="8" fill="transparent" />
                          <circle
                            cx={x}
                            cy={y}
                            r={active ? 6 : 4}
                            fill="#10b981"
                            stroke="#fff"
                            strokeWidth="2"
                          />
                          <title>{`${item?.day ?? ""}: ${item.value}`}</title>
                        </g>
                      );
                    })}
                  </svg>

                  {/* Floating tooltip — now positioned relative to the SVG-only box */}
                  {hover && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${(hover.x / width) * 100}%`,
                        top: `${(hover.y / height) * 100}%`,
                        transform: "translate(-50%, -100%)",
                        bgcolor: "#111827",
                        color: "#fff",
                        fontSize: "0.75rem",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <strong style={{ marginRight: 6 }}>
                        {cleanData[hover.index]?.value}
                      </strong>
                      <span style={{ color: "#9CA3AF" }}>
                        {cleanData[hover.index]?.day ?? ""}
                      </span>
                    </Box>
                  )}
                </Box>

                {/* X-axis labels (outside the positioned SVG box) */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                    px: `${padding}px`,
                  }}
                >
                  {cleanData.map((item, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      color="#6b7280"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {item?.day ?? ""}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Emotion Analysis */}
        <Grid
          item
          size={{ xs: 12, md: 6 }}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Paper
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              flex: 1,
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              fontWeight="600"
              fontSize={"1.125rem"}
            >
              Emotion Analysis
            </Typography>

            <Box sx={{ mt: 3 }}>
              {parameterData?.map((item, index) => (
                <ScoreItem
                  key={index}
                  label={item?.label}
                  score={item?.score}
                  color={item?.color}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        
        {/* Customer Feedback Trends */}
        <Grid
          item
          size={{ xs: 12, md: 6 }}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Paper
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              flex: 1,
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              fontWeight="600"
              fontSize={"1.125rem"}
              mb={2}
            >
              Customer Feedback Trends
            </Typography>

            <FeedbackSection variant="strengths">
              <SectionHeader variant="strengths">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: "#22c55e" }}
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "rgb(17 24 39 / var(--tw-text-opacity, 1))",
                  }}
                >
                  Strengths
                </Typography>
              </SectionHeader>
              <FeedbackList>
                {(
                  sentimentData?.customerFeedbackTrends?.topStrengths ?? []
                ).map((item, index) => (
                  <li key={index}>{item?.text ?? ""}</li>
                ))}
              </FeedbackList>
            </FeedbackSection>

            <FeedbackSection variant="improvements">
              <SectionHeader variant="improvements">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#eab308"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>

                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "rgb(17 24 39 / var(--tw-text-opacity, 1))",
                  }}
                >
                  Areas for Improvement
                </Typography>
              </SectionHeader>
              <FeedbackList>
                {(
                  sentimentData?.customerFeedbackTrends?.topImprovements ?? []
                ).map((item, index) => (
                  <li key={index}>{item?.text ?? ""}</li>
                ))}
              </FeedbackList>
            </FeedbackSection>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

export default Sentiment;
